import type { ApiError, AuthResponse } from '@vm/shared';
import { platform } from '../platform';
import { API_BASE } from './config';

export type ApiErrorCode = NonNullable<ApiError['code']> | 'network' | 'timeout' | 'server' | 'unknown';

const FRIENDLY: Record<ApiErrorCode, string> = {
  unauthorized: 'Your session expired. Please sign in again.',
  forbidden: "You're not allowed to do that.",
  not_found: "We couldn't find that.",
  invalid: "Something doesn't look right. Please check and try again.",
  insufficient_funds: "You don't have enough for that yet.",
  locked: "That's still locked. Keep leveling up!",
  already_owned: 'You already own this.',
  rate_limited: "Whoa, slow down! Try again in a moment.",
  banned: 'This account has been suspended.',
  restricted: "That isn't available on your account. Ask a parent for help.",
  conflict: "That's already taken. Try something else.",
  unavailable: "That isn't available right now. Try again later.",
  update_required: 'A new version of Voxel Marines is out. Update to keep playing online.',
  two_factor_required: 'Confirm this sign-in with the code from your authenticator app.',
  network: "Can't reach the Voxel Marines servers. Check your connection.",
  timeout: 'The server took too long to answer. Please try again.',
  server: 'The server had a problem. Please try again in a moment.',
  unknown: 'Something went wrong. Please try again.',
};

/** Codes whose server message is written for players and is worth showing as-is. */
const SHOW_SERVER_TEXT = new Set<ApiErrorCode>(['invalid', 'conflict', 'locked', 'banned', 'unavailable', 'forbidden', 'restricted', 'insufficient_funds', 'update_required']);

export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: ApiErrorCode,
    readonly serverMessage: string,
  ) {
    super(friendlyMessage(code, serverMessage));
    this.name = 'ApiRequestError';
  }
  get isNetwork(): boolean {
    return this.code === 'network' || this.code === 'timeout';
  }
}

function friendlyMessage(code: ApiErrorCode, server: string): string {
  if (server && SHOW_SERVER_TEXT.has(code) && server.length <= 160 && !/^[a-z_]+$/.test(server)) return server;
  return FRIENDLY[code];
}

/** Turns anything thrown by an API call into a message suitable for a toast. */
export function errorMessage(e: unknown): string {
  if (e instanceof ApiRequestError) return e.message;
  if (e instanceof Error && e.message) return e.message;
  return FRIENDLY.unknown;
}

export function isNetworkError(e: unknown): boolean {
  return e instanceof ApiRequestError && e.isNetwork;
}

export function errorCode(e: unknown): ApiErrorCode | null {
  return e instanceof ApiRequestError ? e.code : null;
}

/**
 * The API turned the session down. Anything else (no connection, a restart during a release answering
 * 502/503, rate limits, a forced update) leaves the session alone: the player stays signed in.
 */
export function isSessionRejected(e: unknown): boolean {
  const code = errorCode(e);
  return code === 'unauthorized' || code === 'forbidden' || code === 'not_found' || code === 'invalid';
}

function codeForStatus(status: number): ApiErrorCode {
  if (status === 401) return 'unauthorized';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'not_found';
  if (status === 409) return 'conflict';
  if (status === 429) return 'rate_limited';
  if (status === 426) return 'update_required';
  if (status === 400 || status === 422) return 'invalid';
  if (status === 402) return 'insufficient_funds';
  if (status === 503) return 'unavailable';
  if (status >= 500) return 'server';
  return 'unknown';
}

// Local storage helpers ----------------------------------------------------------------------

export function readLocal(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

export function writeLocal(key: string, value: string | null): void {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    /* storage unavailable (private mode) — keep going in memory */
  }
}

export function readJson<T>(key: string): T | null {
  const raw = readLocal(key);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

// Device id --------------------------------------------------------------------------------------

function makeId(): string {
  const c = globalThis.crypto as Crypto | undefined;
  // randomUUID needs a secure context (https/localhost); LAN dev URLs fall back to getRandomValues.
  if (c && typeof c.randomUUID === 'function' && window.isSecureContext) return c.randomUUID();
  const b = new Uint8Array(16);
  if (c) c.getRandomValues(b);
  else for (let i = 0; i < 16; i++) b[i] = Math.floor(Math.random() * 256);
  return Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
}

export const deviceId: string = (() => {
  const existing = readLocal('vm.deviceId');
  if (existing) return existing;
  const id = makeId();
  writeLocal('vm.deviceId', id);
  return id;
})();

// Session (access + refresh tokens) ---------------------------------------------------------------

interface Session {
  accessToken: string;
  refreshToken: string;
  /** Epoch ms when the access token expires. */
  expiresAt: number;
}

const SESSION_KEY = 'vm.session';
let session: Session | null = readJson<Session>(SESSION_KEY);

export function hasSession(): boolean {
  return !!session?.refreshToken;
}

/**
 * Another tab of the game may have rotated the tokens since this one loaded: use what it saved. A
 * tab refreshing with the old refresh token would look like token theft and end the session for both.
 */
function adoptStoredSession(): void {
  const stored = readJson<Session>(SESSION_KEY);
  if (stored?.refreshToken && stored.refreshToken !== session?.refreshToken) session = stored;
}

export function accessToken(): string | null {
  return session?.accessToken ?? null;
}

export function refreshToken(): string | null {
  return session?.refreshToken ?? null;
}

export function setSession(res: Pick<AuthResponse, 'accessToken' | 'refreshToken' | 'expiresIn'>): void {
  session = { accessToken: res.accessToken, refreshToken: res.refreshToken, expiresAt: Date.now() + Math.max(10, res.expiresIn) * 1000 };
  writeLocal(SESSION_KEY, JSON.stringify(session));
}

export function clearSession(): void {
  session = null;
  writeLocal(SESSION_KEY, null);
}

type Listener = () => void;
const authLostListeners = new Set<Listener>();
/** Called when the refresh token is rejected: the player must authenticate again. */
export function onAuthLost(fn: Listener): () => void {
  authLostListeners.add(fn);
  return () => authLostListeners.delete(fn);
}

const bannedListeners = new Set<(msg: string) => void>();
export function onBanned(fn: (msg: string) => void): () => void {
  bannedListeners.add(fn);
  return () => bannedListeners.delete(fn);
}

const updateListeners = new Set<(msg: string) => void>();
/** The API refused this app version (HTTP 426, `client.versions` in live config). */
export function onUpdateRequired(fn: (msg: string) => void): () => void {
  updateListeners.add(fn);
  return () => updateListeners.delete(fn);
}

let refreshing: Promise<boolean> | null = null;

/** Exchanges the refresh token for a new access token. Single-flight. */
export function refreshSession(): Promise<boolean> {
  if (refreshing) return refreshing;
  const before = session?.refreshToken;
  adoptStoredSession();
  if (!session?.refreshToken) return Promise.resolve(false);
  // Another tab has just refreshed: its tokens are good, nothing to rotate.
  if (session.refreshToken !== before && session.expiresAt - Date.now() > 30_000) return Promise.resolve(true);
  const rt = session.refreshToken;
  refreshing = (async () => {
    try {
      const res = await rawRequest<AuthResponse>('POST', '/v1/auth/refresh', { refreshToken: rt }, null, 10000);
      setSession(res);
      return true;
    } catch (e) {
      if (isSessionRejected(e)) {
        clearSession();
        for (const fn of authLostListeners) fn();
        return false;
      }
      throw e;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

/** Returns a non-expired access token, refreshing it first when it is about to expire. */
export async function freshAccessToken(): Promise<string | null> {
  adoptStoredSession();
  if (!session) return null;
  if (session.expiresAt - Date.now() < 30_000) {
    const ok = await refreshSession();
    if (!ok) return null;
  }
  return session?.accessToken ?? null;
}

// Requests ------------------------------------------------------------------------------------------

export type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface RequestOptions {
  /** Send the bearer token (default true). */
  auth?: boolean;
  timeoutMs?: number;
  /** Keep the request alive when the page unloads (telemetry). */
  keepalive?: boolean;
}

async function rawRequest<T>(method: Method, path: string, body: unknown, token: string | null, timeoutMs: number, keepalive = false): Promise<T> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  // The version headers let the API turn away apps older than live-ops allows (forced updates).
  const headers: Record<string, string> = { Accept: 'application/json', 'X-Client-Version': __APP_VERSION__, 'X-Client-Platform': platform };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (token) headers.Authorization = `Bearer ${token}`;
  let res: Response;
  try {
    res = await fetch(API_BASE + path, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal: ctrl.signal,
      keepalive,
    });
  } catch (e) {
    const aborted = (e as Error)?.name === 'AbortError';
    throw new ApiRequestError(0, aborted ? 'timeout' : 'network', '');
  } finally {
    clearTimeout(timer);
  }
  const text = await res.text().catch(() => '');
  // Empty bodies resolve to null so callers can tell success (null) from failure (undefined).
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  }
  if (!res.ok) {
    const err = (data && typeof data === 'object' ? data : {}) as Partial<ApiError>;
    const code = (err.code as ApiErrorCode | undefined) ?? codeForStatus(res.status);
    const msg = typeof err.error === 'string' ? err.error : typeof data === 'string' ? data.slice(0, 200) : '';
    if (code === 'banned') for (const fn of bannedListeners) fn(msg);
    if (code === 'update_required') for (const fn of updateListeners) fn(msg);
    throw new ApiRequestError(res.status, code, msg);
  }
  return data as T;
}

/** Binary requests (highlight replays, poster images): bearer auth like `request`, raw bytes both ways. */
export async function requestBytes(method: Method, path: string, body?: Blob, timeoutMs = 30000): Promise<ArrayBuffer> {
  const send = async (token: string | null): Promise<Response> => {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), timeoutMs);
    const headers: Record<string, string> = { 'X-Client-Version': __APP_VERSION__, 'X-Client-Platform': platform };
    if (body) headers['Content-Type'] = body.type || 'application/octet-stream';
    if (token) headers.Authorization = `Bearer ${token}`;
    try {
      return await fetch(API_BASE + path, { method, headers, body, signal: ctrl.signal });
    } catch (e) {
      throw new ApiRequestError(0, (e as Error)?.name === 'AbortError' ? 'timeout' : 'network', '');
    } finally {
      clearTimeout(timer);
    }
  };
  let res = await send(await freshAccessToken());
  if (res.status === 401 && session?.refreshToken && (await refreshSession())) res = await send(session?.accessToken ?? null);
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as Partial<ApiError>;
    throw new ApiRequestError(res.status, (err.code as ApiErrorCode | undefined) ?? codeForStatus(res.status), typeof err.error === 'string' ? err.error : '');
  }
  return res.arrayBuffer();
}

/**
 * Performs an API request. Authenticated calls refresh the access token when it is close to
 * expiring and retry once after a 401.
 */
export async function request<T>(method: Method, path: string, body?: unknown, opts: RequestOptions = {}): Promise<T> {
  const auth = opts.auth !== false;
  const timeout = opts.timeoutMs ?? 12000;
  if (!auth) return rawRequest<T>(method, path, body, null, timeout, opts.keepalive);
  const token = await freshAccessToken();
  try {
    return await rawRequest<T>(method, path, body, token, timeout, opts.keepalive);
  } catch (e) {
    if (e instanceof ApiRequestError && e.code === 'unauthorized' && session?.refreshToken) {
      const ok = await refreshSession();
      if (!ok) throw e;
      return rawRequest<T>(method, path, body, session?.accessToken ?? null, timeout, opts.keepalive);
    }
    throw e;
  }
}
